import React, { useRef, useState } from "react";
import api, { BACKEND_URL, formatErr } from "../lib/api";
import { Upload, X, Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";

export const resolveImageUrl = (u, options = {}) => {
  if (!u) return "";
  
  let url = u;
  if (u.startsWith("/api/")) {
    url = `${BACKEND_URL}${u}`;
  }
  
  // If it's a Supabase storage URL, apply transformations
  if (url.includes(".supabase.co/storage/v1/object/public/")) {
    const { width, height, quality = 80, format = "webp" } = options;
    
    // Only transform if some options are provided (performance boost)
    if (width || height || quality || format) {
      url = url.replace("/object/public/", "/render/image/public/");
      const params = new URLSearchParams();
      if (width) params.append("width", width);
      if (height) params.append("height", height);
      if (quality) params.append("quality", quality);
      if (format) params.append("format", format);
      
      const queryString = params.toString();
      if (queryString) {
        url += (url.includes("?") ? "&" : "?") + queryString;
      }
    }
  }
  
  return url;
};

const MAX_BYTES = 20 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";
const MAX_DIM = 2000;
const COMPRESS_QUALITY = 0.85;
const COMPRESS_THRESHOLD = 500 * 1024; // skip compression if already < 500KB

// Compress via canvas. Returns a File. GIFs and tiny files pass through.
const compressImage = (file) =>
  new Promise((resolve) => {
    if (file.type === "image/gif" || file.size < COMPRESS_THRESHOLD) return resolve(file);
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = () => resolve(file);
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, MAX_DIM / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      const outType = file.type === "image/png" ? "image/jpeg" : file.type;
      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) return resolve(file);
          const ext = outType === "image/jpeg" ? "jpg" : outType.split("/")[1];
          const newName = file.name.replace(/\.[^.]+$/, "") + "." + ext;
          resolve(new File([blob], newName, { type: outType }));
        },
        outType,
        COMPRESS_QUALITY,
      );
    };
    img.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });

export const ImageUpload = ({ value = [], onChange, multiple = true, label = "Images", testId = "image-upload" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const list = Array.isArray(value) ? value : (value ? [value] : []);

  const upload = async (files) => {
    const arr = Array.from(files);
    setBusy(true);
    const uploaded = [];
    try {
      for (const raw of arr) {
        if (!raw.type.startsWith("image/")) { toast.error(`${raw.name}: not an image`); continue; }
        if (raw.size > MAX_BYTES) { toast.error(`${raw.name}: exceeds 20MB`); continue; }
        const f = await compressImage(raw);
        const fd = new FormData();
        fd.append("file", f);
        try {
          const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          uploaded.push(data.url);
          if (f !== raw) {
            const saved = Math.round((1 - f.size / raw.size) * 100);
            if (saved >= 10) toast.message(`Compressed ${raw.name} (${saved}% smaller)`);
          }
        } catch (e) { toast.error(`${raw.name}: ${formatErr(e)}`); }
      }
      if (uploaded.length) {
        const next = multiple ? [...list, ...uploaded] : uploaded.slice(-1);
        onChange(multiple ? next : next[0]);
        toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? "s" : ""}`);
      }
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = (idx) => {
    if (multiple) onChange(list.filter((_, i) => i !== idx));
    else onChange("");
  };

  // HTML5 drag-and-drop reorder (multiple mode only)
  const onItemDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch {}
  };
  const onItemDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onItemDrop = (e, targetIdx) => {
    e.preventDefault(); e.stopPropagation();
    const from = dragIdx;
    setDragIdx(null);
    if (from === null || from === targetIdx) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(targetIdx, 0, moved);
    onChange(next);
  };

  return (
    <div data-testid={testId}>
      {label && <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{label}</div>}

      <div
        onDragOver={(e) => { if (dragIdx !== null) return; e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { if (dragIdx !== null) return; e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 bg-muted/30"}`}
        data-testid={`${testId}-dropzone`}
      >
        {busy ? (
          <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            Uploading...
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
            <Upload className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <div><span className="text-foreground font-medium">Click to upload</span> or drag & drop</div>
            <div className="text-xs">JPG, PNG, WebP, GIF · max 20MB · auto-compressed{multiple ? " · multiple" : ""}</div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple={multiple}
          className="hidden"
          onChange={(e) => e.target.files && upload(e.target.files)}
          data-testid={`${testId}-input`}
        />
      </div>

      {list.length > 0 && (
        <>
          {multiple && list.length > 1 && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-3">
              <GripVertical className="w-3.5 h-3.5" /> Drag thumbnails to reorder · first image is the cover
            </div>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-2" data-testid={`${testId}-previews`}>
            {list.map((u, i) => (
              <div
                key={`${u}-${i}`}
                draggable={multiple && list.length > 1}
                onDragStart={(e) => onItemDragStart(e, i)}
                onDragOver={onItemDragOver}
                onDrop={(e) => onItemDrop(e, i)}
                onDragEnd={() => setDragIdx(null)}
                className={`relative group aspect-square rounded-xl overflow-hidden border bg-muted ${dragIdx === i ? "opacity-40 border-primary" : "border-border"} ${multiple && list.length > 1 ? "cursor-move" : ""}`}
                data-testid={`${testId}-item-${i}`}
              >
                <img src={resolveImageUrl(u)} alt="" className="w-full h-full object-cover pointer-events-none" />
                {i === 0 && multiple && (
                  <span className="absolute bottom-1.5 left-1.5 rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Cover</span>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); remove(i); }}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-foreground/80 hover:bg-destructive text-background flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                  data-testid={`${testId}-remove-${i}`}
                  aria-label="Remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
